import { FlexGrid as Grid, Row, Tile } from '@carbon/react';
import { EmptyState } from 'components/empty-state';
import {
  formatNumber,
  pageRangeText,
  unitRangeText,
} from 'components/search/common/format-helpers';
import plur from 'plur';
import style from './runsheet.module.scss';
import DocumentSvg from 'images/srp-illustration-document.svg';
import { Skeleton } from './runsheet_skeleton';
import { Pagination } from 'components/pagination';
import { InterpretationsModalCallback } from 'components/interpretation-search/interpretation-search-modal';
import { InterpretationRow } from 'components/interpretation-search/interpretations-search';
import { InterpretationSearchAtomValue, OnPaginationChange } from './types';
import { WritableAtom } from 'jotai';
import { useRef, useState } from 'react';
import { useCreateEditedRowsAtomsMap } from './atoms';

// Pagination is document-anchored, so name the unit rather than leaving the
// shared "items" wording that reads as an interpretation count.
const documentRangeText = unitRangeText('document');

interface RunsheetProps {
  searchAtomValue: InterpretationSearchAtomValue;
  actions?: InterpretationsModalCallback;
  onPaginationChange: OnPaginationChange;
  expandedRowsAtom: WritableAtom<{ open: boolean }, [{ open: boolean }], void>;
  titleWorkspace?: number;
  referenceLocationHref?: string;
  openRowInNewTab?: boolean;
  titleWorkspaceHref?: string;
  showEditInterpretationButton?: boolean;
}

export const Runsheet = ({
  searchAtomValue,
  actions,
  onPaginationChange,
  expandedRowsAtom,
  titleWorkspace,
  referenceLocationHref,
  openRowInNewTab,
  titleWorkspaceHref,
  showEditInterpretationButton = false,
}: RunsheetProps) => {
  const searchResult = searchAtomValue;
  // Show the interpretation total in parentheses inside the document range text so
  // both figures read on one line: "1–30 of N documents (M interpretations)".
  const interpretationCount =
    searchResult.data?.aggregations?.interpretationCount;
  const documentRangeTextWithInterpretations = (
    min: number,
    max: number,
    total: number
  ) => {
    return typeof interpretationCount === 'number'
      ? `${documentRangeText(min, max, total)} (${formatNumber(
          interpretationCount
        )} ${plur('interpretation', interpretationCount)})`
      : documentRangeText(min, max, total);
  };
  const refsMap = useRef<Map<number, HTMLElement | null | undefined>>(
    new Map()
  ).current;

  const editedRowsMapAtoms = useCreateEditedRowsAtomsMap(
    (searchResult.data?.results || []).length
  );

  const highlightedRowState = useState<
    { current: number; previous: number } | undefined
  >(undefined);

  const wrapperRef = useRef<HTMLDivElement>(null);

  const onPaginationChangeGoToTop: OnPaginationChange = ({
    page,
    pageSize,
  }) => {
    onPaginationChange({ page, pageSize });

    if (wrapperRef && wrapperRef.current) {
      wrapperRef.current.scrollIntoView();
    }
  };

  return (
    <div ref={wrapperRef} className={style.wrapperRef}>
      <Grid condensed fullWidth className={style.runsheetTable}>
        {searchResult?.data?.results?.length ? (
          <Row className={style.paginationTop}>
            {/* Pager totals count documents, not interpretations: fetching is
                document-anchored, so paging on the interpretation total would
                render empty pages past the last document. The interpretation
                total rides inside the range text in parentheses. */}
            <Pagination
              pageSizes={[30, 60, 100]}
              pageSize={searchResult.data.pageSize}
              page={searchResult.data.page}
              totalItems={searchResult.data.totalCount}
              onChange={onPaginationChangeGoToTop}
              itemRangeText={documentRangeTextWithInterpretations}
              pageRangeText={pageRangeText}
              size="sm"
            />
          </Row>
        ) : null}
        {searchResult.loading && <Skeleton elementCount={10} />}
        {!searchResult.loading &&
          searchResult?.data?.results?.map(
            (documentInterpretationResponse, index) => (
              <InterpretationRow
                key={documentInterpretationResponse.id}
                rows={searchResult.data?.results || []}
                rowIndex={index}
                editedRowsMapAtoms={editedRowsMapAtoms}
                actions={actions}
                expandedRowsAtom={expandedRowsAtom}
                titleWorkspace={titleWorkspace}
                referenceLocationHref={referenceLocationHref}
                openRowInNewTab={openRowInNewTab}
                titleWorkspaceHref={titleWorkspaceHref}
                refsMap={refsMap}
                highlightedRowState={highlightedRowState}
                showEditInterpretationButton={showEditInterpretationButton}
              />
            )
          )}
        {searchResult?.data?.results.length === 0 && (
          <Tile>
            <EmptyState
              headerText="No Data Available"
              helperText="Currently Insights doesn’t have any available data."
              icon={DocumentSvg}
            />
          </Tile>
        )}
        {searchResult?.data?.results?.length ? (
          <Row className={style.gridBottomRow}>
            <Pagination
              pageSizes={[30, 60, 100]}
              pageSize={searchResult.data.pageSize}
              page={searchResult.data.page}
              totalItems={searchResult.data.totalCount}
              onChange={onPaginationChangeGoToTop}
              itemRangeText={documentRangeTextWithInterpretations}
              pageRangeText={pageRangeText}
            />
          </Row>
        ) : null}
      </Grid>
    </div>
  );
};
